/** Knox Service API client */

import i18next from 'i18next';
import { toast } from 'sonner';
import { getKnoxApiUrl } from './debug-mode';

const KNOX_API_BASE = getKnoxApiUrl();

export interface AuthResponse {
  user_id: string;
  org_id?: string;
  name: string;
  email: string;
  avatar?: string;
  matrix_user_id?: string;
  matrix_access_token?: string;
  matrix_homeserver_url?: string;
  default_workspace_id?: string;
  language?: string;
}

export interface LoginParams {
  email: string;
  password: string;
}

export interface RegisterParams {
  email: string;
  password: string;
  name: string;
  invitation_token?: string;
  org_name?: string;
}

export interface InviteInfoResponse {
  id: string;
  applicant_email: string;
  license_days: number;
  member_limit: number;
  expires_at: string;
  is_valid: boolean;
}

export interface OAuthProviderItem {
  provider: string;
  app_key: string;
  display_name?: string;
  login_type?: string;
  extra_config?: string;
}

export interface ListOAuthProvidersResponse {
  providers: OAuthProviderItem[];
}

export interface CheckEmailResponse {
  exists: boolean;
  has_password: boolean;
  auth_providers?: string[];
}

export interface CreateInviteResponse {
  id: string;
  token: string;
  join_code?: string;
  invite_url: string;
  role: string;
  expires_at: string;
  invited_at: string;
}

export interface AcceptInviteResponse {
  org_id: string;
  org_name: string;
  default_workspace_id?: string;
}

export interface Room {
  workspace_id: string;
  matrix_room_id: string;
  room_name: string;
  room_type: 'inbox' | 'room';
  workspace_scope?: 'standalone' | 'workspace';
  description?: string;
  created_by?: string;
  readiness_state?: 'initializing' | 'binding_plugin' | 'waiting_bots' | 'ready' | 'degraded' | 'failed' | 'unsupported';
  readiness_reason?: string;
  can_retry?: boolean;
  retry_action?: string;
  last_error?: string;
}

export interface CreateRoomParams {
  name: string;
  description?: string;
}

export interface DeleteRoomResponse {
  workspace_id: string;
  room_id?: string;
  task_id?: string;
  status: 'queued' | 'already_queued';
}

export interface GenerateSkillFromRoomResponse {
  request_id: string;
  message_body: string;
}

export interface RoomMemory {
  file_name: 'room-memory.md';
  content: string;
  expected_hash: string;
  compatibility_status: string;
  review_status: 'idle' | 'queued' | 'running' | 'failed';
  last_update_source?: 'user' | 'chronicler' | 'external';
  updated_by?: string;
  last_reviewed_at?: string;
  last_memory_updated_at?: string;
}

export type InboxCategory = 'alert' | 'invitation' | 'notification' | 'onboarding' | 'onboarding_side_quest' | 'scheduled_task';
export type InboxPriority = 'critical' | 'high' | 'medium' | 'low';
export type InboxStatus = 'open' | 'resolved' | 'dismissed';

export interface InboxItemAction {
  label?: string;
  action: string;
  payload?: string;
  task_id?: string;
  execution_id?: string;
  task_name?: string;
  status?: string;
  room_id?: string;
  event_id?: string;
}

export interface InboxItem {
  id: string;
  workspace_id: string;
  category: InboxCategory;
  title: string;
  summary?: string;
  status: InboxStatus;
  priority: InboxPriority;
  actions?: string; // JSON string of InboxItemAction[]
  source_type?: string;
  source_ref?: string;
  related_room_id?: string;
  created_by?: string;
  created_at: string;
  content_updated_at?: string;
  updated_at: string;
}

// --- Knowledge types ---

export type KnowledgeScope = 'room' | 'org';
export type KnowledgeLocation = 'cloud' | 'local';
export type KnowledgeStatus = 'draft' | 'approved' | 'rejected' | 'deleted';
export type KnowledgeContentStatus = 'processing' | 'ready' | 'failed';
export type KnowledgeSourceType = 'user_upload' | 'ai_generated';
export type KnowledgeCapability = 'can_view' | 'can_edit_tags' | 'can_delete' | 'can_promote_scope';
export type KnowledgeDenialReason =
  | 'not_room_member'
  | 'not_org_member'
  | 'author_or_org_admin_required'
  | 'knowledge_not_ready'
  | 'knowledge_deleted'
  | 'promotion_room_cloud_only'
  | 'promotion_already_org'
  | 'operation_in_progress';

export interface KnowledgeCapabilities {
  can_view: boolean;
  can_edit_tags: boolean;
  can_delete: boolean;
  can_promote_scope: boolean;
  denial_reasons: Partial<Record<KnowledgeCapability, KnowledgeDenialReason>>;
}

export interface KnowledgeCapabilityConfig {
  max_upload_bytes: number;
  max_inline_preview_bytes: number;
  supported_extensions: string[];
  cloud?: KnowledgeStorageCapability;
  local?: LocalKnowledgeCapability;
}

export interface KnowledgeStorageCapability {
  max_file_bytes: number;
  supported_extensions: string[];
}

export interface LocalKnowledgeAgent {
  agent_id: string;
  name: string;
  online: boolean;
  healthy: boolean;
  max_file_bytes: number;
  supported_extensions: string[] | null;
  diagnostic?: string;
}

export interface LocalKnowledgeCapability {
  enabled: boolean;
  max_files_per_batch: number;
  max_batch_bytes: number;
  agents: LocalKnowledgeAgent[];
}

export interface LocalKnowledgeSource {
  source_id: string;
  agent_id: string;
  agent_name?: string;
  scope: KnowledgeScope;
  room_id?: string;
  display_path: string;
  sync_command: string;
  status: 'creating' | 'active' | 'offline' | 'failed';
  last_synced_at?: string;
  diagnostic?: string;
}

export interface LocalKnowledgeUploadFile {
  file_id: string;
  file_name: string;
  target_relative_path?: string;
  state: 'pending' | 'transferring' | 'received' | 'committed' | 'synced' | 'failed' | 'cancelled';
  knowledge_id?: string;
  error_code?: string;
  error_message?: string;
}

export interface LocalKnowledgeUploadBatch {
  batch_id: string;
  source_id: string;
  state: 'created' | 'uploading' | 'syncing' | 'completed' | 'partial' | 'failed' | 'cancelled';
  created_count?: number;
  updated_count?: number;
  files: LocalKnowledgeUploadFile[];
}

export interface KnowledgeAuthor {
  user_id: string;
  user_name: string;
  avatar_url?: string;
}

export interface KnowledgeItem {
  id: string;
  room_id?: string | null;
  scope: KnowledgeScope;
  location: KnowledgeLocation;
  status: KnowledgeStatus;
  content_status: KnowledgeContentStatus;
  content_version?: string;
  source_type: KnowledgeSourceType;
  title: string;
  file_name?: string;
  file_size?: number;
  mime_type?: string;
  tags: string[];
  assignee_ids: string[];
  author: KnowledgeAuthor;
  capabilities: KnowledgeCapabilities;
  room_name?: string;
  agent_id?: string;
  agent_name?: string;
  agent_online?: boolean;
  source_id?: string;
  source_relative_path?: string;
  local_sync_status?: 'synced' | 'syncing' | 'failed' | 'update_failed' | 'missing' | 'mismatch';
  local_diagnostic?: string;
  created_at: string;
  content_updated_at?: string;
  updated_at: string;
}

export interface ListKnowledgeParams {
  scene: 'knowledge_space' | 'room_desk';
  room_id?: string;
  scope?: KnowledgeScope;
  status?: KnowledgeStatus | 'all';
  source_type?: KnowledgeSourceType;
  q?: string;
  content_status?: KnowledgeContentStatus;
  location?: KnowledgeLocation;
  since?: string;
  offset?: number;
  limit?: number;
  /** @deprecated Use offset instead. Kept until KnowledgePanel migration completes. */
  page?: number;
  /** @deprecated Use limit instead. Kept until KnowledgePanel migration completes. */
  page_size?: number;
}

export interface ListKnowledgeResponse {
  items: KnowledgeItem[];
  total: number;
  offset: number;
  limit: number;
}

export interface KnowledgeDetail extends KnowledgeItem {
  content?: string | null;
  preview_available?: boolean;
  download_available?: boolean;
}

export interface UploadKnowledgeParams {
  file: File;
  scope: KnowledgeScope;
  room_id?: string;
  title?: string;
  tags?: string[];
}

export interface UploadKnowledgeBatchResponse {
  uploaded: Array<{ file_name: string; knowledge: KnowledgeItem; action?: 'created' | 'updated' }>;
  failed: Array<{ file_name: string; code?: string; error: string }>;
  total_uploaded: number;
  total_failed: number;
  created_count?: number;
  updated_count?: number;
}

export interface PromoteKnowledgeParams {
  idempotency_key?: string;
}

export type KnowledgePromotionResult =
  | { status: 'completed'; knowledge: KnowledgeItem }
  | { status: 'converging'; knowledge: KnowledgeItem };

export interface ListRoomsResponse {
  items: Room[];
  total_count: number;
}

export type TaskQueueRunStatus = 'pending' | 'running' | 'cancel_requested' | 'completed' | 'completed_with_errors' | 'cancelled';
export type TaskQueueItemStatus = 'queued' | 'dispatching' | 'running' | 'waiting_human' | 'waiting_approval' | 'retry_wait' | 'cancel_requested' | 'succeeded' | 'failed' | 'timed_out' | 'skipped' | 'cancelled';

export interface TaskQueueSummary {
  total: number;
  queued: number;
  active: number;
  waiting_human: number;
  waiting_approval: number;
  succeeded: number;
  failed: number;
  timed_out: number;
  skipped: number;
  cancelled: number;
}

export interface TaskQueueAttempt {
  attempt: number;
  status: TaskQueueItemStatus;
  conversation_id?: string;
  result?: string;
  error_code?: string;
  error_message?: string;
  started_at?: string;
  completed_at?: string;
}

export interface TaskQueueItem {
  item_id: string;
  run_id: string;
  position: number;
  task_id: string;
  task_number?: string;
  title?: string;
  status: TaskQueueItemStatus;
  attempt: number;
  conversation_id?: string;
  matrix_room_id?: string;
  thread_root_event_id?: string;
  result?: string;
  error_code?: string;
  error_message?: string;
  started_at?: string;
  completed_at?: string;
  interactions?: Record<string, unknown>[];
  attempts: TaskQueueAttempt[];
}

export interface TaskQueueRunResponse {
  run_id: string;
  status: TaskQueueRunStatus;
  version: number;
  terminal: boolean;
  summary: TaskQueueSummary;
  continuation_status?: string;
  execution_persona?: string;
  execution_digital_employee_id?: string;
  originator_persona?: string;
  originator_digital_employee_id?: string;
  started_at?: string;
  completed_at?: string;
  total?: number;
  page?: number;
  page_size?: number;
  items: TaskQueueItem[];
  next_cursor?: string;
}

export interface TaskQueueRunQueryParams {
  query?: string;
  statusGroup?: 'all' | 'completed' | 'incomplete' | 'human';
  page?: number;
  pageSize?: number;
  includeAttempts?: boolean;
}

export interface TaskQueueRunListItem {
  run_id: string;
  parent_task_id?: string;
  task_ids: string[];
  title?: string;
  execution_persona?: string;
  status: TaskQueueRunStatus;
  version: number;
  terminal: boolean;
  summary: TaskQueueSummary;
  created_at: string;
  updated_at: string;
}

export interface WorkspaceTaskStatus {
  id: string;
  name: string;
  color: string;
  category: 'todo' | 'in_progress' | 'done' | 'cancelled' | string;
}

export interface WorkspaceTask {
  id: string;
  workspace_id: string;
  title: string;
  description?: string;
  task_number?: number;
  type?: { id: string; name: string; key: string; icon: string; color: string };
  parent_id?: string;
  parent_title?: string;
  status: WorkspaceTaskStatus;
  assignee?: { id: string; name: string; avatar?: string };
  executor_type?: string;
  executor_id?: string;
  executor?: { id: string; name: string; avatar?: string };
  owner?: { id: string; name: string; avatar?: string };
  priority?: { id: string; name: string; color: string; level: number };
  goal_phase?: string;
  goal_definition?: string;
  execution_requirements?: string;
  creation_source?: string;
  creation_source_ref?: string;
  start_date?: string;
  due_date?: string;
  completed_at?: string;
  tags?: Array<{ id: string; name: string; color: string }>;
  comment_count?: number;
  created_by?: { id: string; name: string; avatar?: string };
  subtask_count: number;
  subtask_completed_count: number;
  created_at: string;
  updated_at: string;
}

export interface WorkspaceTaskListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  statusCategory?: string;
  parentId?: string;
}

export interface WorkspaceTaskListResponse {
  items: WorkspaceTask[];
  total: number;
  page: number;
  page_size: number;
}

export interface WorkspaceInfo {
  id: string;
  name: string;
  description?: string;
  category?: string;
  owner_id?: string;
  role?: string;
  is_member?: boolean;
  is_admin_access?: boolean;
  is_system_workspace?: boolean;
  application_system?: {
    id: string;
    name: string;
    status: 'active' | 'missing' | 'unavailable';
  } | null;
}

export interface WorkspaceListResponse {
  items: WorkspaceInfo[];
  total: number;
  page?: number;
  page_size?: number;
}

export interface WorkspaceWriteParams {
  name: string;
  description?: string;
  application_system_id?: string;
  application_system_name?: string;
}

export interface WorkspaceMemberWriteParams {
  user_id: string;
  role?: 'viewer' | 'editor';
}

export interface UserProfile {
  id?: string;
  email?: string;
  name?: string;
  avatar?: string;
  org_id?: string;
  org_name?: string;
  org_slug?: string;
  role?: string;
  theme?: string;
  language?: string;
}

export interface UpdateProfileParams {
  name?: string;
  avatar?: string;
  theme?: string;
  language?: string;
}

export interface UploadProfileAvatarResponse {
  avatar?: string;
  url?: string;
}

export interface OrgInfo {
  id: string;
  name: string;
  slug: string;
  logo_url?: string;
  role: string;
  status: string;
}

export interface MemberInfo {
  id?: string;
  member_type?: 'user' | 'ai_employee';
  user_id: string;
  name: string;
  email: string;
  avatar?: string;
  role: string;
  status: string;
  joined_at: string;
}

export interface MembersResponse {
  members?: MemberInfo[];
  total?: number;
  active_count?: number;
  disabled_count?: number;
  page?: number;
  page_size?: number;
}

export interface OrgInviteInfo {
  id: string;
  org_id: string;
  org_name: string;
  org_logo?: string;
  inviter_name?: string;
  role: string;
  status: string;
  expires_at: string;
}

export interface OrgJoinInfo {
  organization_id: string;
  organization_name: string;
  role: string;
  type: string;
  category?: string;
  expires_at: string;
  inviter_name: string;
  invitee_email?: string;
  is_valid: boolean;
}

export interface OrgJoinRegisterResponse {
  user_id: string;
  name: string;
  email: string;
  knox_token: string;
  matrix_user_id: string;
  matrix_access_token: string;
  matrix_homeserver_url: string;
  matrix_device_id: string;
  default_workspace_id: string;
  tenant_id: string;
  tenant_name: string;
  tenant_role: string;
}

export interface AccessApplicationData {
  workEmail: string;
  fullName: string;
  companyName: string;
  role: string;
  problem: string;
  teamSize?: string;
}

export interface AccessApplicationResponse {
  id: number;
  status: string;
  message: string;
}

export interface CommandPolicyItem {
  id: number;
  command: string;
  policy: string;
  user_id?: string;
  created_by: string;
  created_by_name?: string;
  created_at: string;
}

/** Known backend error codes → user-facing English messages */
const ERROR_MESSAGES: Record<string, string> = {
  weak_password: 'Password must be at least 12 characters and include at least two of: letters, numbers, and symbols.',
  email_exists: 'An account with this email already exists.',
  invalid_token: 'This invitation code is invalid or expired.',
  invite_expired: 'This invitation has expired.',
  invite_used: 'This invitation has already been used.',
  invalid_credentials: 'Invalid email or password.',
  initialization_failed: 'Workspace setup is still in progress. Please try again.',
  QUOTA_EXCEEDED: 'This organization has reached its member limit.',
  NEED_CHALLENGE: '', // Handled by frontend — suppress toast
};

export class KnoxApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details: Array<{ field: string; message: string }> | Record<string, unknown> = [],
    public latest_revision?: number,
    public conflict_summary?: string,
  ) {
    super(message);
    this.name = 'KnoxApiError';
  }
}

interface RequestOptions extends RequestInit {
  /** When true, suppress the global error toast (use for background polling). */
  silent?: boolean;
  /** reCAPTCHA Enterprise token to inject as X-Recaptcha-Token header. */
  recaptchaToken?: string;
  /** Token type: "checkbox" for v2 fallback, omit for score-based (default). */
  recaptchaType?: 'checkbox';
}

export async function knoxRequest<T>(path: string, init?: RequestOptions): Promise<T> {
  const result = await knoxRequestWithStatus<T>(path, init);
  return result.data;
}

async function knoxRequestWithStatus<T>(path: string, init?: RequestOptions): Promise<{ data: T; status: number }> {
  const { silent, recaptchaToken, recaptchaType, ...fetchInit } = init ?? {};
  const defaultHeaders: HeadersInit = fetchInit?.body instanceof FormData ? {} : { 'Content-Type': 'application/json' };
  // Forward Knox language setting to backend for connector i18n.
  const lang = i18next.language ?? 'en';
  const headers: HeadersInit = {
    ...defaultHeaders,
    'Accept-Language': lang,
    ...(recaptchaToken ? { 'X-Recaptcha-Token': recaptchaToken } : {}),
    ...(recaptchaType ? { 'X-Recaptcha-Type': recaptchaType } : {}),
    ...(fetchInit?.headers ?? {}),
  };
  const res = await fetch(`${KNOX_API_BASE}${path}`, {
    ...fetchInit,
    credentials: 'include',
    headers,
  });
  if (!res.ok) {
    const raw = await res.text();
    let code = '';
    let message = raw || res.statusText;
    let details: Array<{ field: string; message: string }> | Record<string, unknown> = [];
    let latestRevision: number | undefined;
    let conflictSummary: string | undefined;
    try {
      const json = JSON.parse(raw);
      const nestedError = typeof json.error === 'object' && json.error !== null
        ? json.error
        : null;
      code = nestedError?.code ?? json.code ?? '';
      message = ERROR_MESSAGES[code]
        ?? nestedError?.message
        ?? json.message
        ?? (typeof json.error === 'string' ? json.error : message);
      const rawDetails = nestedError?.details ?? json.details;
      if (Array.isArray(rawDetails)) {
        details = rawDetails;
      } else if (rawDetails && typeof rawDetails === 'object') {
        details = rawDetails;
      }
      if (!Array.isArray(details)) {
        if (typeof details.latest_revision === 'number') latestRevision = details.latest_revision;
        if (typeof details.conflict_summary === 'string') conflictSummary = details.conflict_summary;
      }
    } catch {
      // body is not JSON, use raw text
    }
    if (res.status === 413) {
      code = 'PAYLOAD_TOO_LARGE';
      message = 'The upload is too large. Reduce the file batch size and try again.';
    }
    if (res.status === 401 && !path.startsWith('/api/v1/auth/') && window.location.pathname !== '/login') {
      window.location.replace('/login');
      throw new KnoxApiError(res.status, code, message, details, latestRevision, conflictSummary);
    }
    if (!silent && code !== 'NEED_CHALLENGE') {
      toast.error(message, { id: code || message });
    }
    throw new KnoxApiError(res.status, code, message, details, latestRevision, conflictSummary);
  }
  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined as T;
  return { data, status: res.status };
}

async function taskQueueMutationWithRetry<T>(path: string): Promise<T> {
  const retryDelays = [400, 1200];
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await knoxRequest<T>(path, { method: 'POST', body: '{}', silent: true });
    } catch (error) {
      const retryable = error instanceof KnoxApiError && [502, 503, 504].includes(error.status);
      if (!retryable || attempt >= retryDelays.length) {
        if (error instanceof KnoxApiError && error.code !== 'NEED_CHALLENGE') {
          toast.error(error.message, { id: error.code || error.message });
        }
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, retryDelays[attempt]));
    }
  }
}

export interface AuditLogItem {
  id: number;
  org_id: string;
  user_id: string;
  user_name: string;
  workspace_id: string;
  conversation_id: string;
  tool: string;
  connection_id: string;
  channel: string;
  host: string;
  command: string;
  args: string;
  file_path: string;
  exit_code: number | null;
  stdout: string;
  error: string;
  duration_ms: number | null;
  batch_id: string | null;
  batch_total: number | null;
  created_at: string;
}

export interface AuditLogListResponse {
  items: AuditLogItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface WebhookDefinitionItem {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  setup_guide?: string;
  compatible_connectors: string[];
  type: string;
  category: string;
}

export interface WebhookMatchCounts { matched: number; uncertain: number; unmatched: number }
export interface WebhookExecutionSummary {
  id?: string;
  status: string;
  received_at: string;
  completed_at?: string;
  alert_count: number;
  error_summary?: string;
  match_counts: WebhookMatchCounts;
  alerts?: WebhookAlertItem[];
}
export interface WebhookEntityMatch {
  entity_id?: string;
  entity_name?: string;
  entity_display_name?: string;
  entity_display_details?: string[];
  entity_type?: string;
  matched_field?: string;
  matched_value?: string;
  match_method: string;
  confidence: number;
  status: 'matched' | 'uncertain' | 'unmatched';
}
export interface WebhookAlertItem {
  id?: string;
  position: number;
  fingerprint?: string;
  alertname?: string;
  alert_content?: string;
  source_type?: string;
  level?: string;
  status: 'firing' | 'resolved';
  starts_at?: string;
  ends_at?: string;
  labels: Record<string, string>;
  annotations: Record<string, unknown>;
  match_status: 'matched' | 'uncertain' | 'unmatched';
  match_error?: string;
  matches: WebhookEntityMatch[];
  rca_trigger_status?: AlertRcaTriggerStatus;
  rca_room_id?: string;
  rca_trigger_event_id?: string;
  rca_trigger_error?: string;
}
export type AlertRcaTriggerStatus = 'unevaluated' | 'unmatched' | 'triggering' | 'failed' | 'triggered';
export interface AlertItem extends WebhookAlertItem {
  id: string;
  /** Knox-generated webhook_alert_executions.id used by RCA actions. */
  execution_record_id?: string;
  execution_id: string;
  webhook_id: string;
  webhook_deleted?: boolean;
  rca_trigger_status: AlertRcaTriggerStatus;
  created_at: string;
  updated_at: string;
  received_at?: string;
}
export interface AlertListResponse { items: AlertItem[]; total: number; page: number; page_size: number }
export interface StartAlertRcaResponse { status: AlertRcaTriggerStatus; room_id?: string; workspace_id?: string; event_id?: string }
export type AlertFilterRuleField = 'alertname' | 'webhook_id' | 'status' | 'starts_at' | 'label';
export type AlertFilterRuleOperator = 'equals' | 'not_equals' | 'contains' | 'matches' | 'exists' | 'not_exists';
export interface AlertFilterRuleCondition {
  field: AlertFilterRuleField;
  label_key?: string;
  operator: AlertFilterRuleOperator;
  value?: string;
}
export interface AlertFilterRule {
  id: string;
  name: string;
  enabled: boolean;
  conditions: AlertFilterRuleCondition[];
  version: number;
  created_at: string;
  updated_at: string;
}
export interface AlertFilterRuleInput {
  name: string;
  enabled: boolean;
  conditions: AlertFilterRuleCondition[];
}
export interface AlertFilterRuleListResponse { items: AlertFilterRule[] }
export interface ListAlertsParams {
  webhook_id?: string;
  webhook_deleted?: boolean;
  status?: 'firing' | 'resolved';
  rca_trigger_status?: AlertRcaTriggerStatus;
  starts_after?: string;
  starts_before?: string;
  keyword?: string;
  page?: number;
  page_size?: number;
}
export interface WebhookSummary {
  id: string;
  definition_id: string;
  name: string;
  description?: string;
  icon?: string;
  enabled: boolean;
  ingress_url: string;
  connection_id: string;
  connection_name?: string;
  last_triggered_at?: string;
  created_at?: string;
  rate_limit?: number;
}
export interface WebhookDetail extends WebhookSummary {
  ingress_token: string;
  setup_guide?: string;
}
export interface WebhookListResponse { items: WebhookSummary[]; total: number; page: number; page_size: number }
export interface WebhookExecutionListResponse { items: WebhookExecutionDetail[]; total: number; page: number; page_size: number }
export interface WebhookExecutionDetail extends WebhookExecutionSummary {
  request_body?: Record<string, unknown>;
  response_body?: unknown;
  alerts: WebhookAlertItem[];
}
export interface CreateWebhookParams { definition_id: string; name: string; description?: string; connection_id: string }
export interface UpdateWebhookParams { name?: string; description?: string; connection_id?: string }
export interface WebhookTestResult { execution_id?: string; status: string; ingress_token_validated: false; message?: string }
export interface WebhookIngressVerification { execution_id?: string; status: string; ingress_token_validated: true; message?: string }

export type WebhookConfigurationAction = 'configure_and_verify' | 'verify';
export type WebhookConfigurationTaskStatus =
  | 'awaiting_confirmation' | 'inspecting' | 'awaiting_write' | 'configuring'
  | 'validating' | 'reloading' | 'verifying' | 'completed' | 'failed';
export interface WebhookConfigurationStep { name: string; status: 'pending' | 'running' | 'succeeded' | 'failed'; message?: string }
export interface WebhookConfigurationTask {
  id: string;
  webhook_id: string;
  action: WebhookConfigurationAction;
  status: WebhookConfigurationTaskStatus;
  current_step: string;
  steps: WebhookConfigurationStep[];
  write_mode?: '' | 'local_file' | 'k8s' | 'manual';
  plan_summary?: Record<string, unknown>;
  backup_ref?: string;
  verified_execution_id?: string;
  error_code?: string;
  error_detail?: string;
  created_at: string;
}

/** Deduplicate concurrent unparameterized listRooms() calls — multiple
 * useKnoxRooms instances (WorkspaceLayout, KnoxNav, useReplaySender) each
 * fire on mount, sharing a single in-flight promise. */
let listRoomsInFlight: Promise<ListRoomsResponse> | null = null;

/** Deduplicate concurrent countInboxItems() calls — same reasoning as listRooms. */
let countInboxInFlight: Promise<{ count: number }> | null = null;

export const knoxApi = {
  login(params: LoginParams, opts?: { recaptchaToken?: string; recaptchaType?: 'checkbox' }): Promise<AuthResponse> {
    return knoxRequest('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify(params),
      recaptchaToken: opts?.recaptchaToken,
      recaptchaType: opts?.recaptchaType,
    });
  },

  register(params: RegisterParams, opts?: { recaptchaToken?: string; recaptchaType?: 'checkbox'; emailVerifiedToken?: string }): Promise<AuthResponse> {
    return knoxRequest('/api/v1/auth/register', {
      method: 'POST',
      body: JSON.stringify({ ...params, email_verified_token: opts?.emailVerifiedToken }),
      recaptchaToken: opts?.recaptchaToken,
      recaptchaType: opts?.recaptchaType,
    });
  },

  setPassword(email: string, password: string, opts?: { recaptchaToken?: string; recaptchaType?: 'checkbox'; emailVerifiedToken?: string }): Promise<AuthResponse> {
    return knoxRequest('/api/v1/auth/set-password', {
      method: 'POST',
      body: JSON.stringify({ email, password, email_verified_token: opts?.emailVerifiedToken }),
      recaptchaToken: opts?.recaptchaToken,
      recaptchaType: opts?.recaptchaType,
    });
  },

  getSession(opts?: { silent?: boolean }): Promise<AuthResponse> {
    return knoxRequest('/api/v1/auth/session', { silent: opts?.silent });
  },

  getTaskQueueRun(workspaceId: string, runId: string, params: TaskQueueRunQueryParams = {}): Promise<TaskQueueRunResponse> {
    const query = new URLSearchParams({
      include_attempts: String(params.includeAttempts ?? true),
    });
    if (params.query) query.set('query', params.query);
    if (params.statusGroup) query.set('status_group', params.statusGroup);
    if (params.page) query.set('page', String(params.page));
    if (params.pageSize) query.set('page_size', String(params.pageSize));
    return knoxRequest(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/task-queue/runs/${encodeURIComponent(runId)}?${query}`,
      { silent: true },
    );
  },

  listTaskQueueRuns(workspaceId: string, limit = 50): Promise<{ items: TaskQueueRunListItem[] }> {
    return knoxRequest(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/task-queue/runs?limit=${encodeURIComponent(String(limit))}`,
      { silent: true },
    );
  },

  listWorkspaceTasks(workspaceId: string, params: WorkspaceTaskListParams = {}): Promise<WorkspaceTaskListResponse> {
    const query = new URLSearchParams({
      page: String(params.page ?? 1), page_size: String(Math.min(params.pageSize ?? 20, 100)),
      show_completed: 'all', sort_by: 'updated_at', sort_order: 'desc',
    });
    if (params.parentId) query.set('parent_id', params.parentId);
    if (params.search) query.set('search', params.search);
    if (params.statusCategory) query.set('status_category', params.statusCategory);
    return knoxRequest(`/api/v1/elevo/workspaces/${encodeURIComponent(workspaceId)}/tasks?${query}`, { silent: true });
  },

  async listAllWorkspaceTasks(workspaceId: string, params: Omit<WorkspaceTaskListParams, 'page' | 'pageSize'> = {}): Promise<WorkspaceTask[]> {
    const tasks: WorkspaceTask[] = [];
    let page = 1;
    let total = 0;
    do {
      const response = await this.listWorkspaceTasks(workspaceId, { ...params, page, pageSize: 100 });
      tasks.push(...response.items);
      total = response.total;
      page += 1;
    } while (tasks.length < total);
    return tasks;
  },

  listWorkspaceSubtasks(workspaceId: string, parentTaskId: string, params: WorkspaceTaskListParams = {}): Promise<WorkspaceTaskListResponse> {
    return this.listWorkspaceTasks(workspaceId, { ...params, parentId: parentTaskId });
  },

  getWorkspaceTask(workspaceId: string, taskId: string): Promise<WorkspaceTask> {
    return knoxRequest(`/api/v1/elevo/workspaces/${encodeURIComponent(workspaceId)}/tasks/${encodeURIComponent(taskId)}`, { silent: true });
  },

  cancelTaskQueueRun(workspaceId: string, runId: string): Promise<{ run_id: string; status: TaskQueueRunStatus }> {
    return knoxRequest(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/task-queue/runs/${encodeURIComponent(runId)}/cancel`,
      { method: 'POST', body: '{}' },
    );
  },

  cancelTaskQueueItem(workspaceId: string, runId: string, itemId: string): Promise<{ item_id: string; status: TaskQueueItemStatus }> {
    return taskQueueMutationWithRetry(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/task-queue/runs/${encodeURIComponent(runId)}/items/${encodeURIComponent(itemId)}/cancel`,
    );
  },

  retryTaskQueueItem(workspaceId: string, runId: string, itemId: string): Promise<{ item_id: string; status: TaskQueueItemStatus; attempt: number }> {
    return knoxRequest(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/task-queue/runs/${encodeURIComponent(runId)}/items/${encodeURIComponent(itemId)}/retry`,
      { method: 'POST', body: '{}' },
    );
  },

  logout(): Promise<{ ok: boolean }> {
    return knoxRequest('/api/v1/auth/logout', { method: 'POST' });
  },

  oauthAuthorizeUrl(provider: string, redirectUri?: string): string {
    const params = new URLSearchParams();
    if (redirectUri) params.set('redirect_uri', redirectUri);
    const qs = params.toString();
    return `${KNOX_API_BASE}/api/v1/auth/oauth/${provider}/authorize${qs ? '?' + qs : ''}`;
  },

  /**
   * 飞书 JSAPI 免登：前端在飞书客户端内通过 tt.requestAccess/requestAuthCode
   * 拿到免登 code 后，调此端点换取登录会话。
   * 后端用 login_type=jsapi 标记，使 elevoOne feishu connector 换 token 时不传 redirect_uri。
   * 仅飞书客户端内使用；浏览器扫码走 oauthAuthorizeUrl。
   */
  feishuJsapiLogin(provider: string, code: string): Promise<AuthResponse> {
    return knoxRequest(`/api/v1/auth/oauth/${provider}/jsapi-login`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },

  getInviteInfo(token: string): Promise<InviteInfoResponse> {
    return knoxRequest(`/api/v1/auth/invite/${encodeURIComponent(token)}`);
  },

  checkEmail(email: string, opts?: { recaptchaToken?: string }): Promise<CheckEmailResponse> {
    return knoxRequest('/api/v1/auth/check-email', {
      method: 'POST',
      body: JSON.stringify({ email }),
      recaptchaToken: opts?.recaptchaToken,
    });
  },

  listOAuthProviders(): Promise<ListOAuthProvidersResponse> {
    return knoxRequest('/api/v1/auth/providers');
  },

  forgotPassword(email: string, opts?: { recaptchaToken?: string; recaptchaType?: 'checkbox' }): Promise<{ message: string }> {
    return knoxRequest('/api/v1/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
      recaptchaToken: opts?.recaptchaToken,
      recaptchaType: opts?.recaptchaType,
    });
  },

  sendEmailCode(email: string, purpose: 'register' | 'set_password'): Promise<{ message: string }> {
    return knoxRequest('/api/v1/auth/send-email-code', {
      method: 'POST',
      body: JSON.stringify({ email, purpose }),
    });
  },

  verifyEmailCode(email: string, code: string, purpose: 'register' | 'set_password'): Promise<{ email_verified_token: string }> {
    return knoxRequest('/api/v1/auth/verify-email-code', {
      method: 'POST',
      body: JSON.stringify({ email, code, purpose }),
    });
  },

  verifyResetToken(token: string): Promise<{ valid: boolean; email?: string; reason?: string }> {
    return knoxRequest('/api/v1/auth/verify-reset-token', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  },

  resetPassword(token: string, newPassword: string, confirmPassword: string): Promise<{ message: string }> {
    return knoxRequest('/api/v1/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword, confirm_password: confirmPassword }),
    });
  },

  acceptInvite(token: string, orgName: string, orgSlug?: string): Promise<AcceptInviteResponse> {
    return knoxRequest(`/api/v1/auth/invite/${encodeURIComponent(token)}/accept`, {
      method: 'POST',
      body: JSON.stringify({ org_name: orgName, org_slug: orgSlug }),
    });
  },

  createRoom(params: CreateRoomParams): Promise<Room> {
    return knoxRequest('/api/v1/rooms', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },

  listRooms(params?: { search?: string }): Promise<ListRoomsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.set('search', params.search);
    const qs = searchParams.toString();
    if (!qs) {
      // Deduplicate concurrent unparameterized calls — multiple components
      // each fire listRooms() on mount, sharing a single in-flight promise.
      if (!listRoomsInFlight) {
        const request = knoxRequest<ListRoomsResponse>('/api/v1/rooms')
          .finally(() => { listRoomsInFlight = null; });
        listRoomsInFlight = request;
        return request;
      }
      return listRoomsInFlight;
    }
    return knoxRequest<ListRoomsResponse>(`/api/v1/rooms?${qs}`);
  },

  getWorkspace(workspaceId: string): Promise<WorkspaceInfo> {
    return knoxRequest(`/api/v1/elevo/workspaces/${encodeURIComponent(workspaceId)}`);
  },

  createWorkspaceRoom(workspaceId: string, params: CreateRoomParams): Promise<Room> {
    return knoxRequest(`/api/v1/workspaces/${encodeURIComponent(workspaceId)}/rooms`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },

  updateRoomName(workspaceId: string, roomId: string, name: string): Promise<Room> {
    return knoxRequest(`/api/v1/workspaces/${encodeURIComponent(workspaceId)}/rooms/${encodeURIComponent(roomId)}/name`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    });
  },

  deleteRoom(workspaceId: string, roomId: string): Promise<DeleteRoomResponse> {
    return knoxRequest(`/api/v1/workspaces/${encodeURIComponent(workspaceId)}/rooms/${encodeURIComponent(roomId)}`, {
      method: 'DELETE',
    });
  },

  listWorkspaceRooms(workspaceId: string): Promise<ListRoomsResponse> {
    return knoxRequest(`/api/v1/workspaces/${encodeURIComponent(workspaceId)}/rooms`);
  },

  listWorkspaces(params: { keyword?: string; page?: number; page_size?: number } = {}): Promise<WorkspaceListResponse> {
    const searchParams = new URLSearchParams();
    if (params.keyword) searchParams.set('keyword', params.keyword);
    if (params.page) searchParams.set('page', String(params.page));
    if (params.page_size) searchParams.set('page_size', String(params.page_size));
    const query = searchParams.toString();
    return knoxRequest(`/api/v1/elevo/workspaces${query ? `?${query}` : ''}`);
  },

  createWorkspace(params: WorkspaceWriteParams): Promise<WorkspaceInfo> {
    return knoxRequest('/api/v1/elevo/workspaces', {
      method: 'POST',
      body: JSON.stringify({ ...params, category: 'knox_workspace' }),
    });
  },

  updateWorkspace(workspaceId: string, params: WorkspaceWriteParams): Promise<WorkspaceInfo> {
    return knoxRequest(`/api/v1/elevo/workspaces/${encodeURIComponent(workspaceId)}`, {
      method: 'PATCH',
      body: JSON.stringify(params),
    });
  },

  deleteWorkspace(workspaceId: string): Promise<void> {
    return knoxRequest(`/api/v1/elevo/workspaces/${encodeURIComponent(workspaceId)}`, {
      method: 'DELETE',
    });
  },

  getWorkspaceMembers(workspaceId: string, params: { keyword?: string; page?: number; page_size?: number } = {}): Promise<MembersResponse> {
    const searchParams = new URLSearchParams();
    if (params.keyword) searchParams.set('search', params.keyword);
    if (params.page) searchParams.set('page', String(params.page));
    if (params.page_size) searchParams.set('page_size', String(params.page_size));
    const query = searchParams.toString();
    return knoxRequest(`/api/v1/elevo/workspaces/${encodeURIComponent(workspaceId)}/members${query ? `?${query}` : ''}`);
  },

  addWorkspaceMember(workspaceId: string, params: WorkspaceMemberWriteParams): Promise<MemberInfo> {
    return knoxRequest(`/api/v1/elevo/workspaces/${encodeURIComponent(workspaceId)}/members`, {
      method: 'POST',
      body: JSON.stringify({ member_type: 'user', ...params }),
    });
  },

  removeWorkspaceMember(workspaceId: string, memberId: string): Promise<void> {
    return knoxRequest(`/api/v1/elevo/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(memberId)}`, {
      method: 'DELETE',
    });
  },

  retryProvisioning(workspaceId: string, roomId: string): Promise<Room> {
    return knoxRequest(`/api/v1/workspaces/${encodeURIComponent(workspaceId)}/rooms/${encodeURIComponent(roomId)}/retry-provisioning`, {
      method: 'POST',
    });
  },

  getRoomMemory(roomId: string): Promise<RoomMemory> {
    return knoxRequest(`/api/v1/rooms/${encodeURIComponent(roomId)}/memory`);
  },

  saveRoomMemory(roomId: string, content: string, expectedHash: string): Promise<RoomMemory> {
    return knoxRequest(`/api/v1/rooms/${encodeURIComponent(roomId)}/memory`, {
      method: 'PUT',
      body: JSON.stringify({ content, expected_hash: expectedHash }),
    });
  },

  generateSkillFromRoom(roomId: string): Promise<GenerateSkillFromRoomResponse> {
    return knoxRequest(`/api/v1/rooms/${encodeURIComponent(roomId)}/skill-generation`, {
      method: 'POST',
    });
  },

  // --- Elevo proxy APIs (forwarded to elevo-service via knox-service) ---

  getProfile(): Promise<UserProfile> {
    return knoxRequest('/api/v1/elevo/users/me');
  },

  updateProfile(data: UpdateProfileParams): Promise<UserProfile> {
    return knoxRequest('/api/v1/elevo/users/me', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  uploadProfileAvatar(file: File): Promise<UploadProfileAvatarResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('context', 'avatar');
    return knoxRequest('/api/v1/elevo/files/upload', {
      method: 'POST',
      body: formData,
    });
  },

  getMyOrgs(): Promise<{ orgs?: OrgInfo[] }> {
    return knoxRequest('/api/v1/elevo/users/me/orgs');
  },

  listWebhookDefinitions(): Promise<{ items: WebhookDefinitionItem[] }> {
    return knoxRequest('/api/v1/webhook-definitions');
  },

  listWebhooks(params?: { page?: number; page_size?: number; keyword?: string }): Promise<WebhookListResponse> {
    const search = new URLSearchParams();
    if (params?.page) search.set('page', String(params.page));
    if (params?.page_size) search.set('page_size', String(params.page_size));
    if (params?.keyword) search.set('keyword', params.keyword);
    return knoxRequest(`/api/v1/webhooks${search.size ? `?${search}` : ''}`);
  },

  getWebhook(id: string): Promise<WebhookDetail> {
    return knoxRequest(`/api/v1/webhooks/${encodeURIComponent(id)}`);
  },

  createWebhook(params: CreateWebhookParams): Promise<WebhookDetail> {
    return knoxRequest('/api/v1/webhooks', { method: 'POST', body: JSON.stringify(params), silent: true });
  },

  updateWebhook(id: string, params: UpdateWebhookParams): Promise<WebhookDetail> {
    return knoxRequest(`/api/v1/webhooks/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(params), silent: true });
  },

  toggleWebhook(id: string, enabled: boolean): Promise<WebhookSummary> {
    return knoxRequest(`/api/v1/webhooks/${encodeURIComponent(id)}/toggle`, { method: 'POST', body: JSON.stringify({ enabled }) });
  },

  deleteWebhook(id: string): Promise<void> {
    return knoxRequest(`/api/v1/webhooks/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },

  testWebhook(id: string): Promise<WebhookTestResult> {
    return knoxRequest(`/api/v1/webhooks/${encodeURIComponent(id)}/test`, { method: 'POST', body: '{}' });
  },

  verifyWebhookIngress(id: string): Promise<WebhookIngressVerification> {
    return knoxRequest(`/api/v1/webhooks/${encodeURIComponent(id)}/verify-ingress`, { method: 'POST', body: '{}' });
  },

  listWebhookExecutions(id: string, params?: { page?: number; page_size?: number; status?: string; alert_status?: "firing" | "resolved"; starts_after?: string; starts_before?: string; keyword?: string }): Promise<WebhookExecutionListResponse> {
    const search = new URLSearchParams();
    if (params?.page) search.set('page', String(params.page));
    if (params?.page_size) search.set('page_size', String(params.page_size));
    if (params?.status) search.set('status', params.status);
    if (params?.alert_status) search.set('alert_status', params.alert_status);
    if (params?.starts_after) search.set('starts_after', params.starts_after);
    if (params?.starts_before) search.set('starts_before', params.starts_before);
    if (params?.keyword) search.set('keyword', params.keyword);
    return knoxRequest(`/api/v1/webhooks/${encodeURIComponent(id)}/executions${search.size ? `?${search}` : ''}`);
  },

  getWebhookExecution(executionId: string): Promise<WebhookExecutionDetail> {
    return knoxRequest(`/api/v1/webhook-executions/${encodeURIComponent(executionId)}`);
  },

  listAlerts(params: ListAlertsParams = {}): Promise<AlertListResponse> {
    const search = new URLSearchParams();
    if (params.webhook_id) search.set('webhook_id', params.webhook_id);
    if (params.webhook_deleted) search.set('webhook_deleted', 'true');
    if (params.status) search.set('status', params.status);
    if (params.rca_trigger_status) search.set('rca_trigger_status', params.rca_trigger_status);
    if (params.starts_after) search.set('starts_after', params.starts_after);
    if (params.starts_before) search.set('starts_before', params.starts_before);
    if (params.keyword) search.set('keyword', params.keyword);
    if (params.page) search.set('page', String(params.page));
    if (params.page_size) search.set('page_size', String(params.page_size));
    return knoxRequest(`/api/v1/alerts${search.size ? `?${search}` : ''}`);
  },

  getAlert(alertId: string): Promise<AlertItem> {
    return knoxRequest(`/api/v1/alerts/${encodeURIComponent(alertId)}`);
  },

  startAlertRca(alertId: string): Promise<StartAlertRcaResponse> {
    return knoxRequest(`/api/v1/alerts/${encodeURIComponent(alertId)}/rca`, { method: 'POST', body: '{}' });
  },

  ensureAlertRcaRoomAccess(executionId: string): Promise<{ workspace_id: string; room_id: string }> {
    return knoxRequest(`/api/v1/alerts/${encodeURIComponent(executionId)}/room-access`, { method: 'POST', body: '{}' });
  },

  listAlertFilterRules(): Promise<AlertFilterRuleListResponse> {
    return knoxRequest('/api/v1/alert-filter-rules');
  },

  getAlertFilterRule(ruleId: string): Promise<AlertFilterRule> {
    return knoxRequest(`/api/v1/alert-filter-rules/${encodeURIComponent(ruleId)}`);
  },

  createAlertFilterRule(rule: AlertFilterRuleInput): Promise<AlertFilterRule> {
    return knoxRequest('/api/v1/alert-filter-rules', { method: 'POST', body: JSON.stringify(rule) });
  },

  updateAlertFilterRule(ruleId: string, rule: Partial<AlertFilterRuleInput>): Promise<AlertFilterRule> {
    return knoxRequest(`/api/v1/alert-filter-rules/${encodeURIComponent(ruleId)}`, { method: 'PATCH', body: JSON.stringify(rule) });
  },

  deleteAlertFilterRule(ruleId: string): Promise<void> {
    return knoxRequest(`/api/v1/alert-filter-rules/${encodeURIComponent(ruleId)}`, { method: 'DELETE' });
  },

  createWebhookConfigurationTask(id: string, params: { action: WebhookConfigurationAction; confirmed?: boolean; task_id?: string }): Promise<WebhookConfigurationTask> {
    return knoxRequest(`/api/v1/webhooks/${encodeURIComponent(id)}/configuration-tasks`, { method: 'POST', body: JSON.stringify(params) });
  },

  getWebhookConfigurationTask(id: string, taskId: string): Promise<WebhookConfigurationTask> {
    return knoxRequest(`/api/v1/webhooks/${encodeURIComponent(id)}/configuration-tasks/${encodeURIComponent(taskId)}`);
  },

  updateOrg(id: string, data: { name?: string }): Promise<OrgInfo> {
    return knoxRequest(`/api/v1/elevo/orgs/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async getMembers(orgId: string, params?: { keyword?: string; page?: number; page_size?: number }): Promise<MembersResponse> {
    const searchParams = new URLSearchParams();
    if (params?.keyword) searchParams.set('keyword', params.keyword);
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.page_size) searchParams.set('page_size', String(params.page_size));
    const qs = searchParams.toString();
    const response = await knoxRequest<MembersResponse>(`/api/v1/elevo/orgs/${encodeURIComponent(orgId)}/members${qs ? '?' + qs : ''}`);
    return {
      ...response,
      members: (response.members ?? []).map((member) => ({
        ...member,
        user_id: member.user_id || member.id || '',
        name: member.name || member.email || 'Unknown member',
      })),
    };
  },

  updateMember(orgId: string, uid: string, data: { role?: string; status?: string }): Promise<void> {
    return knoxRequest(`/api/v1/elevo/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(uid)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  deleteMember(orgId: string, uid: string): Promise<void> {
    return knoxRequest(`/api/v1/elevo/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(uid)}`, {
      method: 'DELETE',
    });
  },

  createInvite(orgId: string, role: string = 'member', opts?: { regenerate?: boolean }): Promise<CreateInviteResponse> {
    const params = opts?.regenerate ? '?regenerate=true' : '';
    return knoxRequest(`/api/v1/elevo/orgs/${encodeURIComponent(orgId)}/invitations${params}`, {
      method: 'POST',
      body: JSON.stringify({ type: 'link', role }),
    });
  },

  // --- Tenant invitation (Scene 1: admin creates → user registers new org) ---

  getOrgInviteInfo(token: string): Promise<InviteInfoResponse> {
    return knoxRequest(`/api/v1/auth/invite/${encodeURIComponent(token)}`);
  },

  // --- Org member join (ElevoOne org invitations) ---

  getOrgJoinInfo(token: string): Promise<OrgJoinInfo> {
    return knoxRequest(`/api/v1/auth/org-join/${encodeURIComponent(token)}`);
  },


  // --- Logged-in user org creation/joining (no password) ---

  createOrgLoggedIn(params: { token: string; org_name: string; org_slug?: string }): Promise<AuthResponse> {
    return knoxRequest('/api/v1/auth/create-org', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },

  joinOrgLoggedIn(params: { token: string }): Promise<OrgJoinRegisterResponse> {
    return knoxRequest('/api/v1/auth/join-org', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },

  // --- Access application (request invite code) ---

  submitAccessApplication(
    data: AccessApplicationData,
    opts?: { recaptchaToken?: string; recaptchaType?: 'checkbox' },
  ): Promise<AccessApplicationResponse> {
    return knoxRequest('/api/v1/access-applications', {
      method: 'POST',
      body: JSON.stringify(data),
      recaptchaToken: opts?.recaptchaToken,
      recaptchaType: opts?.recaptchaType,
      silent: true,
    });
  },

  // --- Org switching ---

  switchOrg(targetOrgId: string): Promise<{ success: boolean }> {
    return knoxRequest('/api/v1/elevo/auth/switch-org', {
      method: 'POST',
      body: JSON.stringify({ target_org_id: targetOrgId }),
    });
  },

  // --- Copilot tool response (ask_human) ---

  submitToolResponse(params: {
    toolCallId: string;
    conversationId: string;
    answers: Record<string, string>;
  }): Promise<{ success: boolean; message?: string }> {
    return knoxRequest('/api/v1/copilot/tool-response', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },

  // --- Copilot tool approval (HIL) ---

  submitApproval(params: {
    conversationId: string;
    approveId: string;
    approved: boolean;
    scope?: 'once' | 'always';
    reason?: string;
  }): Promise<{ success: boolean; approveId: string; approved: boolean }> {
    return knoxRequest('/api/v1/copilot/approve', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },

  // --- Copilot tool detail (lazy load) ---

  getToolDetail(conversationId: string, toolCallId: string): Promise<{
    toolCallId: string;
    toolName: string;
    title?: string;
    state: string;
    input?: unknown;
    output?: unknown;
    errorText?: string;
    inputSize?: number;
    outputSize?: number;
  }> {
    return knoxRequest(`/api/v1/tools/${encodeURIComponent(conversationId)}/${encodeURIComponent(toolCallId)}`);
  },

  // --- Inbox ---

  createInboxItem(data: {
    category: string;
    title: string;
    summary?: string;
    priority?: string;
    actions?: string;
    source_type?: string;
    source_ref?: string;
    related_room_id?: string;
  }): Promise<InboxItem> {
    return knoxRequest('/api/v1/inbox', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  listInboxItems(params?: { status?: string; limit?: number; offset?: number }): Promise<InboxItem[]> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    const qs = searchParams.toString();
    return knoxRequest<{ items: InboxItem[] }>(`/api/v1/inbox${qs ? '?' + qs : ''}`, { silent: true }).then(resp => resp.items);
  },

  countInboxItems(status: string = 'open'): Promise<{ count: number }> {
    if (!countInboxInFlight) {
      countInboxInFlight = knoxRequest<{ count: number }>(`/api/v1/inbox/count?status=${encodeURIComponent(status)}`, { silent: true }).finally(() => { countInboxInFlight = null; });
    }
    return countInboxInFlight;
  },

  updateInboxItemStatus(id: string, status: 'resolved' | 'dismissed', action?: string): Promise<{ status: string }> {
    return knoxRequest(`/api/v1/inbox/${encodeURIComponent(id)}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, action }),
    });
  },

  updateInboxItemRoom(id: string, relatedRoomId: string): Promise<void> {
    return knoxRequest(`/api/v1/inbox/${encodeURIComponent(id)}/room`, {
      method: 'PATCH',
      body: JSON.stringify({ related_room_id: relatedRoomId }),
    });
  },

  stopAll(roomId: string): Promise<{ stopped: string[] }> {
    return knoxRequest('/api/v1/copilot/stop-all', {
      method: 'POST',
      body: JSON.stringify({ room_id: roomId }),
    });
  },

  stopPersona(roomId: string, persona: string): Promise<{ persona: string; stopped: boolean }> {
    return knoxRequest('/api/v1/copilot/stop', {
      method: 'POST',
      body: JSON.stringify({ room_id: roomId, persona }),
    });
  },

  clearPersona(roomId: string, persona: string): Promise<void> {
    return knoxRequest('/api/v1/copilot/clear', {
      method: 'POST',
      body: JSON.stringify({ room_id: roomId, persona }),
    });
  },

  clearAll(roomId: string): Promise<void> {
    return knoxRequest('/api/v1/copilot/clear-all', {
      method: 'POST',
      body: JSON.stringify({ room_id: roomId }),
    });
  },

  activeTyping(roomId: string): Promise<{ bot_user_ids: string[] }> {
    return knoxRequest(`/api/v1/copilot/active-typing?room_id=${encodeURIComponent(roomId)}`);
  },

  // --- Command Policies ---

  listCommandPolicies(scope?: 'org' | 'personal'): Promise<{ policies: CommandPolicyItem[] }> {
    const params = new URLSearchParams();
    if (scope) params.set('scope', scope);
    const qs = params.toString();
    return knoxRequest<{ policies: CommandPolicyItem[] }>(`/api/v1/command-policies${qs ? '?' + qs : ''}`);
  },

  createCommandPolicy(data: { command: string; policy?: 'allow' | 'block'; scope: 'org' | 'personal' }): Promise<{ id: number; command: string; policy: string }> {
    return knoxRequest('/api/v1/command-policies', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  deleteCommandPolicy(id: number): Promise<void> {
    return knoxRequest(`/api/v1/command-policies/${id}`, { method: 'DELETE' });
  },

  syncMemberAvatar(roomId: string): Promise<{ status: string; avatar_url?: string }> {
    return knoxRequest('/api/v1/rooms/sync-room-profiles', {
      method: 'POST',
      body: JSON.stringify({ room_id: roomId }),
      silent: true,
    });
  },

  listAuditLogs(params?: {
    tool?: string;
    user_id?: string;
    host?: string;
    batch_id?: string;
    status?: string;
    search?: string;
    start_time?: string;
    end_time?: string;
    limit?: number;
    offset?: number;
  }): Promise<AuditLogListResponse> {
    const sp = new URLSearchParams();
    if (params?.tool) sp.set('tool', params.tool);
    if (params?.user_id) sp.set('user_id', params.user_id);
    if (params?.host) sp.set('host', params.host);
    if (params?.batch_id) sp.set('batch_id', params.batch_id);
    if (params?.status) sp.set('status', params.status);
    if (params?.search) sp.set('search', params.search);
    if (params?.start_time) sp.set('start_time', params.start_time);
    if (params?.end_time) sp.set('end_time', params.end_time);
    if (params?.limit) sp.set('limit', String(params.limit));
    if (params?.offset) sp.set('offset', String(params.offset));
    const qs = sp.toString();
    return knoxRequest(`/api/v1/audit-logs${qs ? '?' + qs : ''}`);
  },

  listKnowledge(params: ListKnowledgeParams): Promise<ListKnowledgeResponse> {
    const sp = new URLSearchParams();
    sp.set('scene', params.scene);
    if (params.room_id) sp.set('room_id', params.room_id);
    if (params.scope) sp.set('scope', params.scope);
    if (params.status) sp.set('status', params.status);
    if (params.source_type) sp.set('source_type', params.source_type);
    if (params.q) sp.set('q', params.q);
    if (params.content_status) sp.set('content_status', params.content_status);
    if (params.location) sp.set('location', params.location);
    if (params.since) sp.set('since', params.since);
    const limit = params.limit ?? params.page_size ?? (params.page !== undefined ? 20 : undefined);
    const offset = params.offset ?? (
      params.page !== undefined && limit !== undefined
        ? Math.max(params.page - 1, 0) * limit
        : undefined
    );
    if (offset !== undefined) sp.set('offset', String(offset));
    if (limit !== undefined) sp.set('limit', String(limit));
    return knoxRequest<ListKnowledgeResponse>(`/api/v1/knowledge?${sp.toString()}`, { silent: true });
  },

  getKnowledgeCapabilityConfig(): Promise<KnowledgeCapabilityConfig> {
    return knoxRequest<KnowledgeCapabilityConfig>('/api/v1/knowledge/capabilities');
  },

  ensureLocalKnowledgeSource(params: { agent_id: string; scope: KnowledgeScope; room_id?: string }): Promise<LocalKnowledgeSource> {
    return knoxRequest<LocalKnowledgeSource>('/api/v1/knowledge/local/sources', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },

  uploadLocalKnowledgeBatch(params: { agent_id: string; scope: KnowledgeScope; room_id?: string; files: File[] }): Promise<LocalKnowledgeUploadBatch> {
    const formData = new FormData();
    formData.append('agent_id', params.agent_id);
    formData.append('scope', params.scope);
    if (params.room_id) formData.append('room_id', params.room_id);
    params.files.forEach(file => formData.append('files', file));
    return knoxRequest<LocalKnowledgeUploadBatch>('/api/v1/knowledge/local/upload-batches', {
      method: 'POST',
      body: formData,
    });
  },

  getLocalKnowledgeUploadBatch(batchId: string): Promise<LocalKnowledgeUploadBatch> {
    return knoxRequest<LocalKnowledgeUploadBatch>(`/api/v1/knowledge/local/upload-batches/${encodeURIComponent(batchId)}`, {
      silent: true,
    });
  },

  updateKnowledgeTags(id: string, tags: string[]): Promise<{ id: string; tags: string[] }> {
    return knoxRequest(`/api/v1/knowledge/${encodeURIComponent(id)}/tags`, {
      method: 'PATCH',
      body: JSON.stringify({ tags }),
    });
  },

  uploadKnowledge(params: UploadKnowledgeParams): Promise<KnowledgeItem> {
    const formData = new FormData();
    formData.append('file', params.file);
    formData.append('scope', params.scope);
    if (params.room_id) formData.append('room_id', params.room_id);
    if (params.title) formData.append('title', params.title);
    if (params.tags?.length) formData.append('tags', JSON.stringify(params.tags));
    return knoxRequest<KnowledgeItem>('/api/v1/knowledge', {
      method: 'POST',
      body: formData,
    });
  },

  uploadKnowledgeBatch(params: { files: File[]; scope: KnowledgeScope; room_id?: string; tags?: string[] }): Promise<UploadKnowledgeBatchResponse> {
    const formData = new FormData();
    params.files.forEach(file => formData.append('files', file));
    formData.append('scope', params.scope);
    if (params.room_id) formData.append('room_id', params.room_id);
    if (params.tags?.length) formData.append('tags', JSON.stringify(params.tags));
    return knoxRequest<UploadKnowledgeBatchResponse>('/api/v1/knowledge/batch', { method: 'POST', body: formData });
  },

  uploadRoomAttachment(roomId: string, file: File): Promise<{ path: string; name: string; size: number }> {
    const formData = new FormData();
    formData.append('file', file);
    return knoxRequest(`/api/v1/rooms/${encodeURIComponent(roomId)}/attachments`, {
      method: 'POST',
      body: formData,
    });
  },

  getKnowledgeDetail(id: string): Promise<KnowledgeDetail> {
    return knoxRequest<KnowledgeDetail>(`/api/v1/knowledge/${encodeURIComponent(id)}/detail`);
  },

  deleteKnowledge(id: string): Promise<void> {
    return knoxRequest(`/api/v1/knowledge/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },

  async promoteKnowledge(id: string, params: PromoteKnowledgeParams = {}): Promise<KnowledgePromotionResult> {
    const result = await knoxRequestWithStatus<KnowledgeItem>(`/api/v1/knowledge/${encodeURIComponent(id)}/promote`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return {
      status: result.status === 202 ? 'converging' : 'completed',
      knowledge: result.data,
    };
  },

  updateKnowledgeStatus(id: string, status: 'approved' | 'rejected'): Promise<KnowledgeItem> {
    return knoxRequest<KnowledgeItem>(`/api/v1/knowledge/${encodeURIComponent(id)}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },
};

// --- Credits types ---

export interface CreditBalanceResp {
  balance: number;
  unit: string;
}

export interface RechargeItem {
  id: number;
  amount: number;
  note: string;
  created_at: string;
}

export interface ListRechargesResp {
  total: number;
  items: RechargeItem[];
}

export interface UsageItem {
  period: string;
  quota: number;
  requests: number;
}

export interface ListUsageResp {
  total: number;
  items: UsageItem[];
}

// --- Credits API ---

export const creditsApi = {
  async getBalance(): Promise<CreditBalanceResp> {
    return await knoxRequest<CreditBalanceResp>('/api/v1/credits/balance');
  },

  async listRecharges(page = 1, pageSize = 20): Promise<ListRechargesResp> {
    return await knoxRequest<ListRechargesResp>(`/api/v1/credits/recharges?page=${page}&page_size=${pageSize}`);
  },

  async listUsage(params?: { start?: string; end?: string }): Promise<ListUsageResp> {
    const sp = new URLSearchParams();
    if (params?.start) sp.set('start', params.start);
    if (params?.end) sp.set('end', params.end);
    const qs = sp.toString();
    return await knoxRequest<ListUsageResp>(`/api/v1/credits/usage${qs ? '?' + qs : ''}`);
  },

  async listUsageStats(params?: { start?: string; end?: string }): Promise<{ total_quota: number; total_requests: number }> {
    const sp = new URLSearchParams();
    if (params?.start) sp.set('start', params.start);
    if (params?.end) sp.set('end', params.end);
    const qs = sp.toString();
    return await knoxRequest<{ total_quota: number; total_requests: number }>(`/api/v1/credits/usage/stats${qs ? '?' + qs : ''}`);
  },

  async submitRechargeRequest(reason: string): Promise<{ id: number; status: string }> {
    return await knoxRequest('/api/v1/credits/recharge-requests', {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },
};

// ── Billing API (Stripe credits purchase) ──

interface BillingSubscriptionResp {
  plan: 'free' | 'pro';
  subscription_source: string;
}

interface CreateCreditsCheckoutResp {
  checkout_url: string;
  session_id: string;
}

export interface CheckoutStatusResp {
  status: 'pending' | 'fulfilled' | 'failed' | 'expired' | 'not_found';
  error_msg?: string;
}

export const billingApi = {
  async getSubscription(): Promise<BillingSubscriptionResp> {
    return await knoxRequest<BillingSubscriptionResp>('/api/v1/billing/subscription');
  },

  async createCreditsCheckout(packCredits: number, quantity: number, channel: string, returnUrl: string): Promise<CreateCreditsCheckoutResp> {
    return await knoxRequest<CreateCreditsCheckoutResp>('/api/v1/billing/create-credits-checkout', {
      method: 'POST',
      body: JSON.stringify({ pack_credits: packCredits, quantity, channel, return_url: returnUrl }),
      silent: true, // 错误由 AddCreditsModal handleBuy 自己展示（含中文消息）
    });
  },

  async getCheckoutStatus(sessionId: string): Promise<CheckoutStatusResp> {
    return knoxRequest<CheckoutStatusResp>(
      `/api/v1/billing/checkout-status?session_id=${encodeURIComponent(sessionId)}`,
      { silent: true },
    );
  },

  async listPendingOrders(): Promise<{ orders: PendingOrderItem[] }> {
    return knoxRequest<{ orders: PendingOrderItem[] }>('/api/v1/billing/pending-orders', { silent: true });
  },

  async cancelOrder(sessionId: string): Promise<{ success: boolean }> {
    return knoxRequest<{ success: boolean }>('/api/v1/billing/cancel-order', {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId }),
      silent: true,
    });
  },
};

export interface PendingOrderItem {
  session_id: string;
  credits: number;
  amount_cents: number;
  channel: string;
  created_at: string;
}
